'use client';
import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Mail } from 'lucide-react';

// Social media SVG icons
const FacebookIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);
const TwitterIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
  </svg>
);
const InstagramIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);
const YoutubeIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
    <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white" />
  </svg>
);
const WhatsAppIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

const causeLinks = [
  { label: 'Educational Support', href: '/donation-screen' },
  { label: 'Health & Care Support', href: '/donation-screen' },
  { label: 'Environmental Support', href: '/donation-screen' },
];

const membershipLinks = [
  { label: 'Become a Member', href: '/member-profile-page' },
  { label: 'Member Benefits', href: '/member-profile-page' },
  { label: 'Impact Reports', href: '/member-profile-page' },
  { label: 'Donation Certificates', href: '/member-profile-page' },
];

const orgLinks = [
  { label: 'About MOURNDIECE', href: '/' },
  { label: 'Our Mission', href: '/' },
  { label: 'Campaigns', href: '/' },
  { label: 'News & Updates', href: '/' },
];

const socialLinks = [
  {
    icon: FacebookIcon,
    label: 'Facebook',
    href: 'https://www.facebook.com/mourndiecefoundation',
  },
  {
    icon: TwitterIcon,
    label: 'Twitter',
    href: '#',
  },
  {
    icon: InstagramIcon,
    label: 'Instagram',
    href: 'https://www.instagram.com/mourndiecefoundation',
  },
  {
    icon: YoutubeIcon,
    label: 'YouTube',
    href: '#',
  },
];

export default function Footer() {
  return (
    <footer style={{ background: '#1a1200', color: '#c8b870' }}>
      {/* Main Footer */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <AppLogo size={40} />
              <div>
                <span className="font-extrabold text-lg leading-tight block" style={{ color: '#f5edd0' }}>MOURNDIECE</span>
                <span className="text-xs uppercase tracking-widest" style={{ color: '#a09050' }}>Foundation</span>
              </div>
            </div>
            <p className="text-sm leading-relaxed mb-6" style={{ color: '#a09050' }}>
              Transforming lives through compassionate giving. Every donation creates lasting change in education, health, and our environment.
            </p>
            <div className="flex items-center gap-3">
              {socialLinks?.map(({ icon: IconComponent, label, href }: { icon: React.ComponentType<{ size?: number }>, label: string, href: string }) => (
                <a
                  key={`social-${label}`}
                  href={href}
                  target={href !== '#' ? '_blank' : undefined}
                  rel={href !== '#' ? 'noopener noreferrer' : undefined}
                  aria-label={label}
                  className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-150"
                  style={{ background: '#2e2000', color: '#a09050' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#C49A00'; (e.currentTarget as HTMLElement).style.color = '#ffffff'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#2e2000'; (e.currentTarget as HTMLElement).style.color = '#a09050'; }}
                >
                  {React.createElement(IconComponent, { size: 16 })}
                </a>
              ))}
            </div>
          </div>

          {/* Our Causes */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-5" style={{ color: '#f5edd0' }}>Our Causes</h4>
            <ul className="space-y-3">
              {causeLinks?.map((link) => (
                <li key={`footer-cause-${link?.label}`}>
                  <Link
                    href={link?.href}
                    className="text-sm transition-colors flex items-center gap-2 group"
                    style={{ color: '#a09050' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#C49A00'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#a09050'; }}
                  >
                    <span className="w-1 h-1 rounded-full" style={{ background: '#C49A00' }} />
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Membership */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-5" style={{ color: '#f5edd0' }}>Membership</h4>
            <ul className="space-y-3">
              {membershipLinks?.map((link) => (
                <li key={`footer-member-${link?.label}`}>
                  <Link
                    href={link?.href}
                    className="text-sm transition-colors flex items-center gap-2 group"
                    style={{ color: '#a09050' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#C49A00'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#a09050'; }}
                  >
                    <span className="w-1 h-1 rounded-full" style={{ background: '#C49A00' }} />
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-5" style={{ color: '#f5edd0' }}>Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <Mail size={15} className="shrink-0" style={{ color: '#C49A00' }} />
                <a href="mailto:mourndiecefoundation@gmail.com" className="text-sm transition-colors" style={{ color: '#a09050' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#C49A00'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#a09050'; }}
                >
                  mourndiecefoundation@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <WhatsAppIcon size={15} />
                <a
                  href="https://wa.me/27658178558"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm transition-colors flex items-center gap-2"
                  style={{ color: '#a09050' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#25D366'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#a09050'; }}
                >
                  <span style={{ color: '#25D366' }}>
                    <WhatsAppIcon size={15} />
                  </span>
                  0658178558
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      {/* Bottom Bar */}
      <div className="border-t" style={{ borderColor: '#2e2000' }}>
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs" style={{ color: '#7a6030' }}>
            © 2026 MOURNDIECE Foundation. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-xs transition-colors" style={{ color: '#7a6030' }}>Privacy Policy</a>
            <a href="#" className="text-xs transition-colors" style={{ color: '#7a6030' }}>Terms of Use</a>
            <a href="#" className="text-xs transition-colors" style={{ color: '#7a6030' }}>Cookie Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}